/**
 * The pinned messages popover.
 *
 * Its own query (`pinnedOnly=true`), not a filter over the loaded thread: a pin can sit
 * hundreds of pages back, and filtering the visible window makes the strip appear to
 * empty as the user scrolls.
 *
 * Clicking a row hands the whole message to the surface: a pinned root is focused in the
 * conversation, a pinned reply opens its thread — the pin is a bookmark into the
 * conversation, not a separate object. The strip stays agnostic to which; the surface
 * routes it. Unpin, by contrast, is a channel action the strip owns from its own `cid`
 * (the same `cid` it already sources the pinned list from).
 */

import type { GetUsersByUserIdResponse } from "@birdwatch/client";
import { BlockScroll } from "@birdwatch/ui";
import { cn } from "cn";
import { isEmpty } from "lodash-es";
import pluralize from "pluralize";
import { useState } from "react";
import { useUserDetail } from "@/api/use-user-detail";
import { Button } from "@/components/ui/button/button";
import {
  BwCloseLine,
  BwPin1Fill,
  BwPinListLine,
  BwPinRemoveLine,
} from "@/components/ui/icons";
import { Markdown } from "@/components/ui/markdown";
import {
  Message,
  MessageAttachment,
  MessageDocumentList,
} from "@/components/ui/message";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Typography } from "@/components/ui/typography";
import { AudioRecordAttachment } from "@/features/shared/audio-recorder";
import type { ChannelMessage } from "../_api/channel-endpoints";
import { useChannelPinnedMessages } from "../_api/use-channel-messages";
import { useChannelMessageInteractions } from "../_hooks/use-channel-message-interactions";
import { splitAttachments } from "../_libs/message-attachments";
import { ChannelAuthorAvatar, ChannelAuthorName } from "./channel-author";
import { ChannelMessageActions } from "./channel-message-actions";
import { ChannelMessageUnpinDialog } from "./channel-message-unpin-dialog";

export type ChannelPinnedStripProps = {
  cid: string;
  onSelectMessage: (message: ChannelMessage) => void;
  /** Copy for the unpin confirmation dialog. Defaults to the "message" wording. */
  unpinConfirm?: { title?: string; description?: string };
  className?: string;
};

export function ChannelPinnedStrip({
  cid,
  onSelectMessage,
  unpinConfirm,
  className,
}: ChannelPinnedStripProps) {
  const [open, setOpen] = useState(false);
  const { data: pinnedMessages } = useChannelPinnedMessages(cid);
  const {
    togglePin,
    messagePendingUnpin,
    dismissUnpin,
    unpinMessage,
    isUnpinning,
  } = useChannelMessageInteractions(cid);

  if (isEmpty(pinnedMessages)) return null;

  const count = pinnedMessages?.length ?? 0;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button
              kind="ghost"
              variant="neutral"
              size="sm"
              data-test="channel-pinned-trigger"
              className={className}
            >
              <BwPinListLine />
              <Typography size="sm">{count}</Typography>
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent data-test="channel-pinned-tooltip">
          {count} pinned {pluralize("comment", count)}
        </TooltipContent>
      </Tooltip>

      <PopoverContent
        align="end"
        data-test="channel-pinned-content"
        onCloseAutoFocus={(event) => event.preventDefault()}
        className={cn(
          /* sorted */
          "flex flex-col",
          "w-90 max-h-100",
          "p-0",
        )}
      >
        <div
          className={cn(
            /* sorted */
            "flex items-center gap-2",
            "shrink-0",
            "px-4 py-3",
          )}
        >
          <Typography size="base" className="grow font-medium">
            Pinned comments
          </Typography>
          <Button
            size="icon-md"
            kind="ghost"
            variant="neutral"
            data-test="channel-pinned-close"
            onClick={() => setOpen(false)}
          >
            <BwCloseLine />
          </Button>
        </div>

        <BlockScroll className="p-1">
          {pinnedMessages?.map((message) => (
            <ChannelPinnedRow
              key={message.id}
              message={message}
              onSelect={() => {
                onSelectMessage(message);
                setOpen(false);
              }}
              onUnpin={() => togglePin(message)}
            />
          ))}
        </BlockScroll>

        {/* Nested inside the popover so Radix treats the confirm modal as a branch of
              the popover's dismissable layer — opening it must not close the popover. */}
        <ChannelMessageUnpinDialog
          message={messagePendingUnpin}
          open={messagePendingUnpin != null}
          onOpenChange={(next) => {
            if (!next) dismissUnpin();
          }}
          isUnpinning={isUnpinning}
          onConfirm={unpinMessage}
          unpinConfirm={unpinConfirm}
        />
      </PopoverContent>
    </Popover>
  );
}

type ChannelPinnedRowProps = {
  message: ChannelMessage;
  onSelect: () => void;
  onUnpin: () => void;
};

function ChannelPinnedRow({
  message,
  onSelect,
  onUnpin,
}: ChannelPinnedRowProps) {
  const { data: author } = useUserDetail(message.userCreatedId, {
    enabled: !!message.userCreatedId,
  });
  // Resolves the pinner from the same cached useUserDetail rows as the author/avatar.
  const { data: pinner } = useUserDetail(message.pinnedById ?? "", {
    enabled: !!message.pinnedById,
  });
  const { audioAttachments, mediaAttachments, documentAttachments } =
    splitAttachments(message.attachments);

  return (
    <div
      role="button"
      tabIndex={0}
      data-test="channel-pinned-item"
      onClick={onSelect}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onSelect();
        }
      }}
      className={cn(
        /* sorted */
        "group relative",
        "cursor-pointer",
        "rounded-lg hover:bg-bg-dark-min",
      )}
    >
      <ChannelMessageActions
        actions={[{ title: "Unpin", icon: BwPinRemoveLine, onClick: onUnpin }]}
      />
      <div
        className={cn(
          /* sorted */
          "flex items-center gap-1",
          "pt-2 px-3 xl:px-4 pl-14",
          "text-text-dark-low",
        )}
      >
        <BwPin1Fill size={14} />
        <Typography size="xs" className="text-text-dark-low">
          {pinner?.firstName ? `Pinned by ${pinner.firstName}` : "Pinned"}
        </Typography>
      </div>
      <Message
        avatar={<ChannelAuthorAvatar userId={message.userCreatedId} />}
        userCreateName={<ChannelAuthorName userId={message.userCreatedId} />}
        viaClient={message.viaClient ?? ""}
        createdAt={message.createdAt}
        updatedAt={message.updatedAt}
        isNew={false}
      >
        {!isEmpty(mediaAttachments) && (
          <MessageAttachment attachments={mediaAttachments as never} />
        )}

        <Markdown content={message.body ?? ""} className="text-sm" />

        {audioAttachments.map((attachment) => (
          <AudioRecordAttachment
            key={attachment.id}
            attachment={attachment as never}
            userCreated={author as GetUsersByUserIdResponse}
          />
        ))}

        {!isEmpty(documentAttachments) && (
          <MessageDocumentList attachments={documentAttachments as never} />
        )}
      </Message>
    </div>
  );
}
